const DEFAULT_API_ERROR_MESSAGE = 'An error occurred. Please try again.';

/**
 * Mirrors the Swift SMBackendError formats from the Samaritan API,
 * plus a few legacy shapes used by the webapp.
 *
 * Top-level shape: { errors: <variant> } | { message | error | text: string }
 */

interface BackendV3Error {
    description: string;
}

interface BackendAttributeError {
    attribute: string;
    message: string;
}

interface BackendResourceError {
    resource: string;
    attribute: string;
    description: string;
}

interface BackendErrorResponse {
    errors?: unknown;
    message?: string;
    error?: string;
    text?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isV3ErrorArray(errors: unknown): errors is BackendV3Error[] {
    return (
        Array.isArray(errors) &&
        errors.length > 0 &&
        isRecord(errors[0]) &&
        typeof errors[0].description === 'string'
    );
}

function isAttributeErrorArray(
    errors: unknown
): errors is BackendAttributeError[] {
    return (
        Array.isArray(errors) &&
        errors.length > 0 &&
        isRecord(errors[0]) &&
        typeof errors[0].attribute === 'string' &&
        typeof errors[0].message === 'string'
    );
}

function isResourceErrorArray(
    errors: unknown
): errors is BackendResourceError[] {
    return (
        Array.isArray(errors) &&
        errors.length > 0 &&
        isRecord(errors[0]) &&
        typeof errors[0].resource === 'string' &&
        typeof errors[0].attribute === 'string' &&
        typeof errors[0].description === 'string'
    );
}

function isModelSpecificErrors(
    errors: unknown
): errors is Record<string, string[]> {
    if (!isRecord(errors)) return false;
    const values = Object.values(errors);
    return (
        values.length > 0 &&
        values.every(
            (v) => Array.isArray(v) && v.every((s) => typeof s === 'string')
        )
    );
}

/**
 * Decodes the `errors` field from an SMBackendErrorResponse.
 * Tries each variant in the same order as the Swift SMBackendError decoder.
 */
export function decodeBackendErrors(errors: unknown): string | null {
    if (isV3ErrorArray(errors)) {
        return errors[0].description;
    }

    if (isAttributeErrorArray(errors)) {
        return errors[0].message;
    }

    if (isResourceErrorArray(errors)) {
        return errors[0].description;
    }

    if (typeof errors === 'string' && errors.length > 0) {
        return errors;
    }

    // e.g. string[] like ["Phone: Has already been taken"]
    if (
        Array.isArray(errors) &&
        errors.length > 0 &&
        typeof errors[0] === 'string'
    ) {
        return errors[0];
    }

    // e.g. { base: ["..."] } or { phone: ["..."] }
    if (isModelSpecificErrors(errors)) {
        const firstMessages = Object.values(errors)[0];
        return firstMessages?.[0] ?? null;
    }

    return null;
}

/**
 * Normalize an API error payload (or thrown Error) into a user-facing string.
 */
export function parseApiErrorMessage(
    body: unknown,
    fallback: string = DEFAULT_API_ERROR_MESSAGE
): string {
    if (!body) return fallback;
    if (typeof body === 'string' && body.length > 0) return body;

    if (!isRecord(body)) return fallback;

    const data = body as BackendErrorResponse;

    const backendMessage = decodeBackendErrors(data.errors);
    if (backendMessage) return backendMessage;

    if (typeof data.message === 'string' && data.message.length > 0) {
        return data.message;
    }

    // Legacy / alternate top-level keys used by some web endpoints
    if (typeof data.error === 'string' && data.error.length > 0) {
        return data.error;
    }

    if (typeof data.text === 'string' && data.text.length > 0) {
        return data.text;
    }

    return fallback;
}

/** Alias matching the Swift-side naming. */
export const decodeApiErrorMessage = parseApiErrorMessage;

/**
 * Reads and decodes the error message from a failed fetch Response.
 */
export async function decodeApiErrorFromResponse(
    response: Response,
    fallback?: string
): Promise<string> {
    const defaultFallback =
        fallback ?? `HTTP error! status: ${response.status}`;
    try {
        const body = await response.json();
        return parseApiErrorMessage(body, defaultFallback);
    } catch {
        return defaultFallback;
    }
}
